/** Corner radius of the editor bento-tray tiles (canvas tile, header tray,
 *  properties column). Persisted in localStorage and applied as a
 *  `--bento-radius` custom property on <html>, which `style.css` maps to the
 *  Tailwind `rounded-bento` utility those tiles use. Kept separate from the
 *  global `--radius` token so buttons, inputs and dialogs keep their own
 *  rounding. Mirrors font-size-preference.ts (preview vs. persist split).
 *  Default: medium (the pre-preference `rounded-lg` look). */

const STORAGE_KEY = 'stitchpress-bento-radius';

export const BENTO_RADIUS_CSS_VAR = '--bento-radius';

export const BENTO_RADII = ['none', 'small', 'medium', 'large', 'x-large'] as const;
export type BentoRadius = (typeof BENTO_RADII)[number];

/** Rem so the corners track the UI font-size preference like the bento gap. */
export const BENTO_RADIUS_REM: Record<BentoRadius, string> = {
    none: '0rem',
    small: '0.25rem',
    medium: '0.5rem',
    large: '1rem',
    'x-large': '1.5rem',
};

type Listener = (radius: BentoRadius) => void;
const listeners = new Set<Listener>();

function isBentoRadius(value: string | null): value is BentoRadius {
    return (BENTO_RADII as readonly string[]).includes(value ?? '');
}

export function getBentoRadius(): BentoRadius {
    try {
        const stored = window.localStorage.getItem(STORAGE_KEY);
        return isBentoRadius(stored) ? stored : 'medium';
    } catch {
        return 'medium';
    }
}

/** Apply without persisting — a live preview. Notifies subscribers so any
 *  mounted picker reflects the previewed value. */
export function applyBentoRadius(radius: BentoRadius): void {
    applyRootRadius(radius);
    for (const l of listeners) l(radius);
}

/** Persist without applying. Call only on an explicit commit (Apply), so an
 *  unapplied preview never survives a reload — same reasoning as
 *  `persistFontSize`. */
export function persistBentoRadius(radius: BentoRadius): void {
    try {
        window.localStorage.setItem(STORAGE_KEY, radius);
    } catch {
        // Ignore storage failures (private mode, etc.).
    }
}

/** Persist and apply in one step. */
export function setBentoRadius(radius: BentoRadius): void {
    persistBentoRadius(radius);
    applyBentoRadius(radius);
}

export function onBentoRadiusChange(listener: Listener): () => void {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

/** Apply the current persisted preference to <html>. Call once at startup. */
export function initBentoRadius(): void {
    applyRootRadius(getBentoRadius());
}

function applyRootRadius(radius: BentoRadius): void {
    if (typeof document === 'undefined') return;
    document.documentElement.style.setProperty(BENTO_RADIUS_CSS_VAR, BENTO_RADIUS_REM[radius]);
}
